import { Component } from "@theme/component";

/**
 * @typedef {Object} AnchorEntry
 * @property {HTMLAnchorElement} button
 * @property {HTMLElement} target
 */

/** Sentinel must clear this buffer before unpinning (prevents boundary flicker). */
const UNPIN_TOP_BUFFER = 4;

class TopicNavigation extends Component {
  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {HTMLElement | null} */
  #headerSection = null;

  /** @type {AnchorEntry[]} */
  #anchorEntries = [];

  /** @type {IntersectionObserver | null} */
  #pinObserver = null;

  /** @type {boolean} */
  #isPinned = false;

  connectedCallback() {
    super.connectedCallback();
    this.#headerSection = document.querySelector(
      "#header-group .header-section"
    );
    this.#buildAnchorEntries();
    this.#setupPinObserver();
    this.#updateActiveButton();

    this.#updateTopicNavigationHeight();

    document.addEventListener("scroll", this.#handleScroll, { passive: true });
    window.addEventListener("resize", this.#handleScroll, { passive: true });
    window.addEventListener("resize", this.#updateTopicNavigationHeight);
    window.addEventListener("pageshow", this.#handlePageShow);
    window.addEventListener("load", this.#handleLoad, { once: true });
    document.addEventListener("scroll", this.#handleInitialScroll, {
      passive: true,
      once: true
    });

    this.buttonElements.forEach((button) => {
      button.addEventListener("click", this.#handleButtonClick);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener("scroll", this.#handleScroll);
    window.removeEventListener("resize", this.#handleScroll);
    window.removeEventListener("resize", this.#updateTopicNavigationHeight);
    window.removeEventListener("pageshow", this.#handlePageShow);

    this.#pinObserver?.disconnect();
    this.#pinObserver = null;

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    this.#headerSection?.classList.remove("header-concealed");
    this.dataset.stickyState = "inactive";
    this.#headerSection = null;
    this.#anchorEntries = [];
    this.#isPinned = false;

    const shopifySection = this.closest(".shopify-section");

    if (shopifySection instanceof HTMLElement) {
      shopifySection.style.removeProperty("--topic-navigation-slot-height");
      shopifySection.classList.remove("topic-navigation--pinned");
    }

    this.buttonElements.forEach((button) => {
      button.removeEventListener("click", this.#handleButtonClick);
    });
  }

  #updateTopicNavigationHeight = () => {
    document.body.style.setProperty(
      "--topic-navigation-height",
      `${this.offsetHeight}px`
    );
  };

  #setupPinObserver() {
    const section = this.closest(".shopify-section");
    const sentinel = section?.querySelector(".topic-navigation__pin-sentinel");

    if (!(sentinel instanceof HTMLElement)) return;

    this.#pinObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#updatePinStateFromEntry(entry);
      },
      { threshold: 0 }
    );

    this.#pinObserver.observe(sentinel);
    this.#schedulePinStateSync();
  }

  #handlePageShow = () => {
    this.#schedulePinStateSync();
  };

  #handleLoad = () => {
    this.#schedulePinStateSync();
  };

  #handleInitialScroll = () => {
    this.#syncPinState();
  };

  #schedulePinStateSync() {
    requestAnimationFrame(() => {
      this.#syncPinState();

      requestAnimationFrame(() => {
        this.#syncPinState();

        setTimeout(() => this.#syncPinState(), 100);
      });
    });
  }

  #syncPinState() {
    const section = this.closest(".shopify-section");
    const sentinel = section?.querySelector(".topic-navigation__pin-sentinel");

    if (!(sentinel instanceof HTMLElement)) return;

    const { top } = sentinel.getBoundingClientRect();

    this.#applyPinStateFromSentinelTop(top);
  }

  /**
   * @param {IntersectionObserverEntry} entry
   */
  #updatePinStateFromEntry(entry) {
    this.#applyPinStateFromSentinelTop(entry.boundingClientRect.top);
  }

  /**
   * Pin once the sentinel has scrolled above the viewport; unpin when it returns.
   * @param {number} sentinelTop
   */
  #applyPinStateFromSentinelTop(sentinelTop) {
    if (this.#isPinned) {
      if (sentinelTop > UNPIN_TOP_BUFFER) {
        this.#applyPinnedState(false);
      }
      return;
    }

    if (sentinelTop <= 0) {
      this.#applyPinnedState(true);
    }
  }

  /**
   * @param {boolean} isPinned
   */
  #applyPinnedState(isPinned) {
    if (this.#isPinned === isPinned) return;

    const shopifySection = this.closest(".shopify-section");

    if (isPinned && shopifySection instanceof HTMLElement) {
      shopifySection.style.setProperty(
        "--topic-navigation-slot-height",
        `${shopifySection.offsetHeight}px`
      );
      shopifySection.classList.add("topic-navigation--pinned");
    } else if (shopifySection instanceof HTMLElement) {
      shopifySection.style.removeProperty("--topic-navigation-slot-height");
      shopifySection.classList.remove("topic-navigation--pinned");
    }

    this.#isPinned = isPinned;
    this.dataset.stickyState = isPinned ? "active" : "inactive";
    this.#headerSection?.classList.toggle("header-concealed", isPinned);

    requestAnimationFrame(() => this.#updateTopicNavigationHeight());
  }

  /**
   * @param {MouseEvent} event
   */
  #handleButtonClick = (event) => {
    const button =
      event.currentTarget instanceof HTMLAnchorElement
        ? event.currentTarget
        : null;
    this.#setActiveButton(button);
  };

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;

      this.#syncPinState();
      this.#updateActiveButton();
    });
  };

  #buildAnchorEntries() {
    this.#anchorEntries = [];

    for (const button of this.buttonElements) {
      if (!(button instanceof HTMLAnchorElement)) continue;

      const hash = new URL(button.href, window.location.href).hash;

      if (!hash) continue;

      const target = document.getElementById(hash.slice(1));

      if (!target) continue;

      this.#anchorEntries.push({ button, target });
    }
  }

  /**
   * @param {HTMLAnchorElement | null} activeButton
   */
  #setActiveButton(activeButton) {
    for (const button of this.buttonElements) {
      button.classList.toggle("active", button === activeButton);

      if (button === activeButton) {
        button.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
          // @ts-ignore
          container: "nearest"
        });
      }
    }
  }

  #getTopicNavigationHeight() {
    const value = getComputedStyle(document.body).getPropertyValue(
      "--topic-navigation-height"
    );
    const height = parseFloat(value);

    return Number.isFinite(height) ? height : this.offsetHeight;
  }

  #updateActiveButton() {
    if (this.#anchorEntries.length === 0) return;

    /** @type {HTMLAnchorElement | null} */
    let activeButton = null;

    if (this.#isPinned) {
      const scrollY = document.scrollingElement?.scrollTop ?? 0;
      const scrollPosition = scrollY + this.#getTopicNavigationHeight();

      for (const { button, target } of this.#anchorEntries) {
        const scrollMarginTop =
          parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        const triggerPoint =
          target.getBoundingClientRect().top + scrollY - scrollMarginTop;

        if (triggerPoint <= scrollPosition) {
          activeButton = button;
        }
      }
    } else {
      const activationLine = this.getBoundingClientRect().bottom;

      for (const { button, target } of this.#anchorEntries) {
        if (target.getBoundingClientRect().top <= activationLine) {
          activeButton = button;
        }
      }
    }

    this.#setActiveButton(activeButton);
  }

  get buttonElements() {
    return this.querySelectorAll(".pills__pill");
  }
}

customElements.define("topic-navigation", TopicNavigation);
